/*
 * Pure rules for the personal Notifications page (`/notifications`).
 *
 * The server stores OVERRIDES only (`f_notification_preferences`): one row per
 * module, submodule or event, each field `null` = inherit. The page edits a
 * DRAFT copy of those rows and sends it back as a whole-set PUT.
 *
 * Resolution on the client mirrors the server's ladder for the person's own
 * rows — event row → submodule row → module row — and falls back to the
 * server's `effective` answer for everything below that (group default, role
 * default, catalogue default). The server's `effective` is per event, not per
 * field, so a field whose saved value came from one of this person's rows and
 * whose row the draft has since dropped is shown with the saved value until the
 * next save answers. "Reset to team defaults" saves at once for that reason.
 *
 * Key formats (contract 2026-10-16): module `<module>`, submodule
 * `<module>.<submodule>`, event = the catalogue event key.
 *
 * Changes are counted per EVENT by what the reader sees (email, in-app,
 * frequency, scope), not by row diffs, so a switch flipped and flipped back
 * counts as nothing.
 *
 * No access (owner 2026-10-22): an event whose `effective.access` is `none`
 * (its grant module reads as `none` for this person's live role) is shown off
 * whatever the rows say, its controls are disabled, and module / submodule
 * switches skip it. Stored rows are left alone, so a regained access brings the
 * old choice back. Locked wins over no access.
 */
import type {
  NotificationEffectivePreference,
  NotificationEventInfo,
  NotificationFrequency,
  NotificationModule,
  NotificationPreferenceRow,
  NotificationPreferenceSource,
  NotificationPreferencesResponse,
  NotificationScope,
  NotificationSubmodule,
  NotificationTargetType,
  NotificationUserSettings,
} from '@/lib/types';

export type PrefField = 'emailEnabled' | 'inboxEnabled' | 'frequency' | 'scope';
export type ChannelField = 'emailEnabled' | 'inboxEnabled';

export type Draft = ReadonlyMap<string, NotificationPreferenceRow>;

export interface EventView {
  email: boolean;
  inbox: boolean;
  frequency: NotificationFrequency;
  scope: NotificationScope;
  locked: boolean;
  /** Forced off: the person's permission set does not include the event's module. */
  noAccess: boolean;
  /** `user` when any shown field comes from this person's own rows. */
  source: NotificationPreferenceSource | null;
}

export const FREQUENCY_OPTIONS: readonly { value: NotificationFrequency; label: string }[] = [
  { value: 'instant', label: 'Instant' },
  { value: 'daily', label: 'Daily summary' },
  { value: 'weekly', label: 'Weekly summary' },
];

export const SCOPE_OPTIONS: readonly { value: NotificationScope; label: string }[] = [
  { value: 'mine', label: 'My clients' },
  { value: 'all', label: 'All clients' },
];

/** ISO weekdays, 1 = Monday. */
export const WEEKDAYS: readonly { value: number; short: string; long: string }[] = [
  { value: 1, short: 'Mon', long: 'Monday' },
  { value: 2, short: 'Tue', long: 'Tuesday' },
  { value: 3, short: 'Wed', long: 'Wednesday' },
  { value: 4, short: 'Thu', long: 'Thursday' },
  { value: 5, short: 'Fri', long: 'Friday' },
  { value: 6, short: 'Sat', long: 'Saturday' },
  { value: 7, short: 'Sun', long: 'Sunday' },
];

export const SOURCE_LABELS: Record<NotificationPreferenceSource, string | null> = {
  user: null,
  group: 'Team default',
  role: 'Role default',
  default: 'Default',
  locked: null,
  no_access: 'No access',
};

export function lockedReason(moduleKey: string): string {
  return moduleKey === 'security' ? 'Required for account security' : 'Required';
}

/**
 * Disabled reason on a no-access control. `personName` null = your own page.
 */
export function noAccessReason(moduleLabel: string, personName: string | null): string {
  return personName === null
    ? `Your permission set does not include ${moduleLabel}.`
    : `${personName}'s permission set does not include ${moduleLabel}.`;
}

/** Saved answer says the person cannot read this event's module. Locked events never are. */
export function isNoAccess(saved: NotificationPreferencesResponse, ev: NotificationEventInfo): boolean {
  if (ev.locked) return false;
  const base = saved.effective[ev.key];
  return base !== undefined && !base.locked && base.access === 'none';
}

/** Every event in the module is no access (and there is at least one). */
export function moduleNoAccess(saved: NotificationPreferencesResponse, mod: NotificationModule): boolean {
  const events = mod.submodules.flatMap((sub) => sub.events);
  return events.length > 0 && events.every((ev) => isNoAccess(saved, ev));
}

export function rowKey(targetType: NotificationTargetType, targetKey: string): string {
  return `${targetType}:${targetKey}`;
}

export function submoduleKey(mod: NotificationModule, sub: NotificationSubmodule): string {
  return `${mod.key}.${sub.key}`;
}

export function draftFromRows(rows: readonly NotificationPreferenceRow[]): Map<string, NotificationPreferenceRow> {
  const map = new Map<string, NotificationPreferenceRow>();
  for (const row of rows) map.set(rowKey(row.targetType, row.targetKey), { ...row });
  return map;
}

export function rowsFromDraft(draft: Draft): NotificationPreferenceRow[] {
  return [...draft.values()].filter(
    (row) =>
      row.emailEnabled !== null ||
      row.inboxEnabled !== null ||
      row.frequency !== null ||
      row.scope !== null,
  );
}

function chainKeys(mod: NotificationModule, sub: NotificationSubmodule, ev: NotificationEventInfo): string[] {
  return [rowKey('event', ev.key), rowKey('submodule', submoduleKey(mod, sub)), rowKey('module', mod.key)];
}

function catalogueFallback(ev: NotificationEventInfo): NotificationEffectivePreference {
  return {
    email: ev.locked || (ev.defaultOn && ev.defaultChannels.includes('email')),
    inbox: ev.locked || (ev.defaultOn && ev.defaultChannels.includes('inbox')),
    frequency: ev.locked ? 'instant' : ev.defaultFrequency,
    scope: 'mine',
    locked: ev.locked,
    source: ev.locked ? 'locked' : 'default',
  };
}

function savedEffective(
  saved: NotificationPreferencesResponse,
  ev: NotificationEventInfo,
): NotificationEffectivePreference {
  return saved.effective[ev.key] ?? catalogueFallback(ev);
}

function pick<F extends PrefField>(
  draft: Draft,
  keys: readonly string[],
  field: F,
): NotificationPreferenceRow[F] | null {
  for (const key of keys) {
    const value = draft.get(key)?.[field];
    if (value !== null && value !== undefined) return value;
  }
  return null;
}

export function viewOf(
  draft: Draft,
  saved: NotificationPreferencesResponse,
  mod: NotificationModule,
  sub: NotificationSubmodule,
  ev: NotificationEventInfo,
): EventView {
  const base = savedEffective(saved, ev);
  const locked = ev.locked || base.locked;
  if (locked) {
    return {
      email: true,
      inbox: true,
      frequency: 'instant',
      scope: base.scope,
      locked: true,
      noAccess: false,
      source: 'locked',
    };
  }
  if (base.access === 'none') {
    return {
      email: false,
      inbox: false,
      frequency: base.frequency,
      scope: base.scope,
      locked: false,
      noAccess: true,
      source: 'no_access',
    };
  }
  const keys = chainKeys(mod, sub, ev);
  const email = pick(draft, keys, 'emailEnabled');
  const inbox = pick(draft, keys, 'inboxEnabled');
  const frequency = pick(draft, keys, 'frequency');
  const scope = ev.clientEvent ? pick(draft, keys, 'scope') : null;
  const fromUser = email !== null || inbox !== null || frequency !== null || scope !== null;
  return {
    email: email ?? base.email,
    inbox: inbox ?? base.inbox,
    frequency: frequency ?? base.frequency,
    scope: scope ?? base.scope,
    locked: false,
    noAccess: false,
    source: fromUser ? 'user' : base.source === 'user' ? null : base.source,
  };
}

export function sameView(a: EventView, b: EventView, clientEvent: boolean): boolean {
  return (
    a.email === b.email &&
    a.inbox === b.inbox &&
    a.frequency === b.frequency &&
    (!clientEvent || a.scope === b.scope)
  );
}

function withField<F extends PrefField>(
  draft: Map<string, NotificationPreferenceRow>,
  targetType: NotificationTargetType,
  targetKey: string,
  field: F,
  value: NotificationPreferenceRow[F] | null,
) {
  const key = rowKey(targetType, targetKey);
  const existing = draft.get(key) ?? {
    targetType,
    targetKey,
    emailEnabled: null,
    inboxEnabled: null,
    frequency: null,
    scope: null,
  };
  const next = { ...existing, [field]: value } as NotificationPreferenceRow;
  if (
    next.emailEnabled === null &&
    next.inboxEnabled === null &&
    next.frequency === null &&
    next.scope === null
  ) {
    draft.delete(key);
  } else {
    draft.set(key, next);
  }
}

/** One field on one event row. */
export function setEventField<F extends PrefField>(
  draft: Draft,
  ev: NotificationEventInfo,
  field: F,
  value: NonNullable<NotificationPreferenceRow[F]>,
): Map<string, NotificationPreferenceRow> {
  const next = new Map(draft);
  withField(next, 'event', ev.key, field, value);
  return next;
}

/**
 * A module or submodule channel switch. Sets every event below it. When the
 * group holds a locked event the value is written per event instead of on the
 * group row, so the server never sees a group-level "off" over a locked event.
 * No-access events (`noAccessKeys`) are skipped the same way: their stored rows
 * stay untouched and no group row is written over them.
 */
export function setGroupChannel(
  draft: Draft,
  mod: NotificationModule,
  sub: NotificationSubmodule | null,
  field: ChannelField,
  value: boolean,
  noAccessKeys: ReadonlySet<string> = new Set(),
): Map<string, NotificationPreferenceRow> {
  const next = new Map(draft);
  const subs = sub ? [sub] : mod.submodules;
  const events = subs.flatMap((s) => s.events);
  const skipped = (ev: NotificationEventInfo) => ev.locked || noAccessKeys.has(ev.key);

  if (events.some(skipped)) {
    for (const ev of events) {
      if (!skipped(ev)) withField(next, 'event', ev.key, field, value);
    }
    return next;
  }

  if (sub) {
    withField(next, 'submodule', submoduleKey(mod, sub), field, value);
  } else {
    withField(next, 'module', mod.key, field, value);
    for (const s of mod.submodules) withField(next, 'submodule', submoduleKey(mod, s), field, null);
  }
  for (const ev of events) withField(next, 'event', ev.key, field, null);
  return next;
}

/** `no_access` = every event below is no access: shown off and disabled. */
export type GroupState = 'on' | 'off' | 'mixed' | 'locked' | 'no_access';

export function groupState(views: readonly EventView[], field: ChannelField): GroupState {
  if (views.length > 0 && views.every((view) => view.noAccess)) return 'no_access';
  const open = views.filter((view) => !view.locked && !view.noAccess);
  if (open.length === 0) return 'locked';
  const key = field === 'emailEnabled' ? 'email' : 'inbox';
  const on = open.filter((view) => view[key]).length;
  if (on === 0) return 'off';
  if (on === open.length) return 'on';
  return 'mixed';
}

/** Draft minus every row inside the module. */
export function withoutModule(draft: Draft, mod: NotificationModule): Map<string, NotificationPreferenceRow> {
  const next = new Map(draft);
  next.delete(rowKey('module', mod.key));
  for (const sub of mod.submodules) {
    next.delete(rowKey('submodule', submoduleKey(mod, sub)));
    for (const ev of sub.events) next.delete(rowKey('event', ev.key));
  }
  return next;
}

/** Draft rows that belong to this module. */
export function moduleHasRows(rows: Iterable<NotificationPreferenceRow>, mod: NotificationModule): boolean {
  const keys = new Set<string>([rowKey('module', mod.key)]);
  for (const sub of mod.submodules) {
    keys.add(rowKey('submodule', submoduleKey(mod, sub)));
    for (const ev of sub.events) keys.add(rowKey('event', ev.key));
  }
  for (const row of rows) {
    if (keys.has(rowKey(row.targetType, row.targetKey))) return true;
  }
  return false;
}

export function sameSettings(a: NotificationUserSettings, b: NotificationUserSettings): boolean {
  return (
    a.dailyTime === b.dailyTime &&
    a.weeklyDay === b.weeklyDay &&
    a.weeklyTime === b.weeklyTime &&
    a.emailPaused === b.emailPaused &&
    [...a.dailyDays].sort().join(',') === [...b.dailyDays].sort().join(',')
  );
}

/** Settings keys that differ, for a minimal PUT. */
export function settingsPatch(
  saved: NotificationUserSettings,
  draft: NotificationUserSettings,
): Partial<NotificationUserSettings> | null {
  const patch: Partial<NotificationUserSettings> = {};
  if (saved.dailyTime !== draft.dailyTime) patch.dailyTime = draft.dailyTime;
  if ([...saved.dailyDays].sort().join(',') !== [...draft.dailyDays].sort().join(',')) {
    patch.dailyDays = [...draft.dailyDays].sort((x, y) => x - y);
  }
  if (saved.weeklyDay !== draft.weeklyDay) patch.weeklyDay = draft.weeklyDay;
  if (saved.weeklyTime !== draft.weeklyTime) patch.weeklyTime = draft.weeklyTime;
  if (saved.emailPaused !== draft.emailPaused) patch.emailPaused = draft.emailPaused;
  return Object.keys(patch).length > 0 ? patch : null;
}
